import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useEffect, useState } from 'react';

export default function NotFoundPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => { document.title = 'Page Not Found | OneCheckUp'; }, []);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 60); return () => clearTimeout(t); }, []);

  // If not authenticated, redirect to login
  useEffect(() => {
    if (!currentUser) {
      const t = setTimeout(() => navigate('/login', { replace: true }), 2500);
      return () => clearTimeout(t);
    }
  }, [currentUser, navigate]);

  const hasClinicalProfile = !!currentUser?.doctor_id;
  const role = currentUser?.role;

  const homeRoute = () => {
    if (!currentUser) return '/login';
    if (hasClinicalProfile) return '/';
    if (role === 'staff') return '/frontdesk';
    return '/settings';
  };

  const quickLinks = (() => {
    if (!currentUser) return [];
    const links = [];
    if (hasClinicalProfile || role === 'staff') {
      links.push({
        label: 'Patients',
        route: role === 'staff' ? '/frontdesk/patients' : '/patients',
      });
      links.push({
        label: 'Appointments',
        route: role === 'staff' ? '/frontdesk/appointments' : '/appointments',
      });
    }
    if (hasClinicalProfile) {
      links.push({ label: 'Prescriptions', route: '/prescriptions' });
    }
    if (role === 'admin') {
      links.push({ label: 'Settings', route: '/settings' });
      links.push({ label: 'Billing', route: '/settings' });
    }
    return links;
  })();

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'hsl(var(--background))',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        fontFamily: '"IBM Plex Sans", sans-serif',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle grid background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(hsl(var(--border) / 0.35) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border) / 0.35) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
        }}
      />

      {/* Content */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          maxWidth: '480px',
          width: '100%',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(16px)',
          transition: 'opacity 0.5s ease, transform 0.5s ease',
        }}
      >
        {/* Large 404 — watermark style */}
        <div style={{ textAlign: 'center', marginBottom: '0.25rem', lineHeight: 1, position: 'relative' }}>
          <span
            style={{
              fontSize: 'clamp(6rem, 22vw, 10rem)',
              fontWeight: 800,
              letterSpacing: '-0.06em',
              color: 'hsl(var(--primary) / 0.08)',
              userSelect: 'none',
              display: 'block',
              lineHeight: 1,
            }}
          >
            404
          </span>

          {/* ECG line overlaid on 404 */}
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg
              viewBox="0 0 320 40"
              style={{ width: 'min(320px, 90%)', height: '40px', overflow: 'visible' }}
              fill="none"
            >
              <polyline
                points="0,20 40,20 55,20 60,5 67,35 74,5 79,32 85,20 130,20 152,20 160,2 168,38 176,2 182,30 190,20 230,20 280,20 320,20"
                stroke="hsl(var(--primary))"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                style={{
                  strokeDasharray: 700,
                  strokeDashoffset: visible ? 0 : 700,
                  transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1) 0.3s',
                }}
              />
              {/* Pulse dot */}
              <circle
                cx="182"
                cy="30"
                r="3"
                fill="hsl(var(--primary))"
                style={{
                  opacity: visible ? 1 : 0,
                  transition: 'opacity 0.3s ease 1.4s',
                }}
              />
            </svg>
          </div>
        </div>

        {/* Title + description */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1
            style={{
              fontSize: '1.25rem',
              fontWeight: 700,
              color: 'hsl(var(--foreground))',
              marginBottom: '0.5rem',
              letterSpacing: '-0.025em',
            }}
          >
            Page not found
          </h1>
          <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', lineHeight: 1.6, margin: 0 }}>
            {!currentUser
              ? 'Redirecting you to login…'
              : <>The path <code style={{ fontFamily: 'monospace', fontSize: '0.8rem', background: 'hsl(var(--muted))', padding: '1px 6px', borderRadius: '4px', color: 'hsl(var(--foreground))' }}>{location.pathname}</code> doesn't exist.</>
            }
          </p>
        </div>

        {/* Primary action */}
        <div style={{ display: 'flex', gap: '0.625rem', justifyContent: 'center', marginBottom: '2rem' }}>
          <button
            onClick={() => window.history.back()}
            style={{
              padding: '0.5rem 1.125rem',
              fontSize: '0.8125rem',
              fontWeight: 500,
              borderRadius: '6px',
              border: '1px solid hsl(var(--border))',
              background: 'hsl(var(--card))',
              color: 'hsl(var(--foreground))',
              cursor: 'pointer',
              transition: 'background 0.15s, border-color 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'hsl(var(--muted))')}
            onMouseLeave={e => (e.currentTarget.style.background = 'hsl(var(--card))')}
          >
            ← Go back
          </button>
          <button
            onClick={() => navigate(homeRoute())}
            style={{
              padding: '0.5rem 1.125rem',
              fontSize: '0.8125rem',
              fontWeight: 500,
              borderRadius: '6px',
              border: '1px solid hsl(var(--primary))',
              background: 'hsl(var(--primary))',
              color: 'hsl(var(--primary-foreground))',
              cursor: 'pointer',
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.opacity = '0.88')}
            onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
          >
            {!currentUser ? 'Go to Login' : 'Go to Dashboard'}
          </button>
        </div>

        {/* Quick links — role-aware */}
        {quickLinks.length > 0 && (
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500 }}>
              Quick navigate
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' }}>
              {quickLinks.map(({ label, route }) => (
                <button
                  key={label}
                  onClick={() => navigate(route)}
                  style={{
                    padding: '0.3125rem 0.75rem',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    borderRadius: '999px',
                    border: '1px solid hsl(var(--border))',
                    background: 'transparent',
                    color: 'hsl(var(--muted-foreground))',
                    cursor: 'pointer',
                    transition: 'color 0.15s, border-color 0.15s, background 0.15s',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.color = 'hsl(var(--primary))';
                    e.currentTarget.style.borderColor = 'hsl(var(--primary) / 0.4)';
                    e.currentTarget.style.background = 'hsl(var(--primary) / 0.05)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.color = 'hsl(var(--muted-foreground))';
                    e.currentTarget.style.borderColor = 'hsl(var(--border))';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Branding footer */}
        <p style={{ textAlign: 'center', marginTop: '3rem', fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground) / 0.5)', letterSpacing: '0.04em' }}>
          ONECHECKUP · CLINIC MANAGEMENT
        </p>
      </div>
    </div>
  );
}
